import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, TextField, Typography } from '@material-ui/core';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import { PasswordStrengthErrors } from '€lib/strings/types';
import { requestSignUp } from '€slices/sign-up';
import { getSigningUp, getSignUpError, getSignUpFailed } from '€slices/sign-up/sign-up-selectors';
import { passwordStrength } from '€lib/strings/passwordStrength';
import ContentLayout from '€layouts/ContentLayout';
import PrimaryButton from '€components/PrimaryButton';
import PasswordStrength from '€components/PasswordStrength';

import CenterSmUp from '€components/CenterSmUp';

type Props = {
    token: string;
    email: string;
};

const SignUpForm = ({ token, email }: Props) => {
    const { register, handleSubmit, getValues } = useForm({
        mode: 'onChange',
    });

    const dispatch = useDispatch();
    const router = useRouter();

    const signingUp = useSelector(getSigningUp);
    const signupFailed = useSelector(getSignUpFailed);
    const signupError = useSelector(getSignUpError);
    const [passwordErrors, setPasswordErrors] = useState<PasswordStrengthErrors>({});
    const [passwordsValid, setPasswordsValid] = useState<boolean>(false);

    useEffect(() => {
        const passwordStr = passwordStrength('', '');
        setPasswordErrors(passwordStr[1]);
        setPasswordsValid(passwordStr[0]);
    }, []);

    const onSubmit = useCallback(
        async ({ password }) => {
            const { a: appId } = router.query;
            try {
                await dispatch(
                    requestSignUp({
                        password,
                        token,
                        username: email,
                        appId: String(appId),
                    }),
                );
            } catch (e) {
                // TODO: catch error or convert above to use `await to()`
            }
        },
        [dispatch, email, token, router.query],
    );

    const handleValidate = useCallback(() => {
        const { password, confirmPassword } = getValues();
        const strength = passwordStrength(password, confirmPassword);

        setPasswordErrors(strength[1]);
        setPasswordsValid(strength[0]);

        return true;
    }, [getValues]);

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <ContentLayout.Form>
                    <ContentLayout.FormField>
                        <TextField
                            required
                            inputRef={register({
                                required: true,
                                validate: {
                                    isOk: handleValidate,
                                },
                            })}
                            type="password"
                            name="password"
                            label="Password"
                            autoFocus
                        />
                    </ContentLayout.FormField>
                    <ContentLayout.FormField>
                        <TextField
                            required
                            inputRef={register({
                                required: true,
                                validate: {
                                    isOk: handleValidate,
                                },
                            })}
                            type="password"
                            name="confirmPassword"
                            label="Confirm Password"
                        />
                    </ContentLayout.FormField>
                    <PasswordStrength errors={passwordErrors} />
                    <ContentLayout.Action>
                        <Box display="flex" justifyContent="center">
                            <PrimaryButton
                                size={['full', 'large']}
                                type="submit"
                                disabled={!passwordsValid || signingUp}
                            >
                                Let&apos;s start saving
                            </PrimaryButton>
                        </Box>
                    </ContentLayout.Action>
                    {signupFailed && (
                        <CenterSmUp>
                            <Typography color="error">{String(signupError)}</Typography>
                        </CenterSmUp>
                    )}
                </ContentLayout.Form>
            </form>
        </>
    );
};

export default SignUpForm;
